import WithErrorHandling from "@/modules/common/components/wrappers/with-error-handling";
import useFetchSubDepartments from "@/modules/common/hooks/subdepartments-data.hook";
import { SubDepartment } from "@/modules/common/types";
import { GetOrganizationTargetResponse } from "@/modules/targets/types";
import { muiTheme } from "@/styles/theme";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Skeleton,
  Typography,
} from "@mui/material";
import { PlusCircle } from "lucide-react";
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import Grid from "@mui/material/GridLegacy";

interface OrganizationTargetProps {
  viewOnly?: boolean;
  months: string[];
  onCreateOrganizationTarget: (department: {
    subDepartmentUId: string;
    subDepartmentName: string;
  }) => void;
  organizationTargets: GetOrganizationTargetResponse | undefined;
  isLoadingOrganizationTargets: boolean;
  isErrorFetchingOrganizationTargets: boolean;
  onError?: () => void;
}

interface DepartmentTarget {
  name: string;
  uid: string;
  m1: number;
  m2: number;
  m3: number;
  total: number;
}

const OrganizationTarget: React.FC<OrganizationTargetProps> = ({
  viewOnly,
  months,
  onCreateOrganizationTarget,
  organizationTargets,
  isLoadingOrganizationTargets,
  isErrorFetchingOrganizationTargets,
  onError,
}) => {
  const { uid } = useParams();
  const { subDepartments: eligibleSubDepartmentsForTargets } = useFetchSubDepartments({
    organizationUId: uid,
    hasTarget: true,
  });

  const getQuarterDetails = (dateStr: Date) => {
    const date = new Date(dateStr);
    const month = date.getMonth();
    const quarter = Math.floor(month / 3) + 1;
    const quarterStartMonth = Math.floor(month / 3) * 3;
    const quarterEndMonth = quarterStartMonth + 2;

    return {
      quarter,
      startMonth: quarterStartMonth,
      endMonth: quarterEndMonth,
      monthInQuarter: month - quarterStartMonth,
    };
  };

  const organizePeriodData = useMemo(() => {
    if (!organizationTargets?.data?.length) return { departments: [] };

    const departmentMap = new Map<string, DepartmentTarget>();

    organizationTargets.data.forEach((target) => {
      const deptName = target.SubDepartment.name;
      const quarterInfo = getQuarterDetails(target.from);
      const monthPosition = quarterInfo.monthInQuarter;

      if (!departmentMap.has(deptName)) {
        departmentMap.set(deptName, {
          name: deptName,
          uid: target.SubDepartment.uid,
          m1: 0,
          m2: 0,
          m3: 0,
          total: 0,
        });
      }

      const dept = departmentMap.get(deptName)!;

      // Assign amount to the appropriate month
      switch (monthPosition) {
        case 0:
          dept.m1 = target.amount;
          break;
        case 1:
          dept.m2 = target.amount;
          break;
        case 2:
          dept.m3 = target.amount;
          break;
      }

      dept.total = dept.m1 + dept.m2 + dept.m3;
    });

    return {
      departments: Array.from(departmentMap.values()),
    };
  }, [organizationTargets?.data]);

  if (isErrorFetchingOrganizationTargets && onError) {
    onError();
  }

  return (
    <Card
      sx={
        !viewOnly
          ? {
              bgcolor: muiTheme.colorSchemes.light.palette.main.white,
              boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
              overflow: "hidden",
            }
          : {
              bgcolor: muiTheme.colorSchemes.light.palette.main.white,
              boxShadow: "none",
              borderRadius: 0,
              padding: 0,
              overflow: "hidden",
            }
      }
    >
      <CardHeader
        title={
          <Box
            sx={{
              color: muiTheme.colorSchemes.light.palette.neutral[900],
              ...muiTheme.typography.h3_med,
            }}
          >
            Organization Target
          </Box>
        }
      />
      <CardContent
        sx={{
          overflow: "auto",
          "&::-webkit-scrollbar": {
            height: "8px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
        }}
      >
        <Box sx={{ minWidth: "800px" }}>
          <Grid container spacing={1}>
            {/* Header row */}
            <Grid item mobile={2.4}>
              <Box
                sx={{
                  p: 1,
                  bgcolor: muiTheme.colorSchemes.light.palette.neutral[50],
                  borderRadius: 1,
                  height: 50,
                }}
              >
                <Box
                  sx={{
                    color: muiTheme.colorSchemes.light.palette.neutral[700],
                    ...muiTheme.typography.h4_med,
                  }}
                >
                  Sub department
                </Box>
              </Box>
            </Grid>
            {months.map((month) => (
              <Grid item mobile={2.4} key={month}>
                <Box
                  sx={{
                    borderRadius: 1,
                    p: 1,
                    pl: 2,
                    bgcolor: "#EAF3EB",
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      color: muiTheme.colorSchemes.light.palette.neutral[700],
                      ...muiTheme.typography.h4_med,
                    }}
                  >
                    {month}
                  </Typography>
                </Box>
              </Grid>
            ))}
            <Grid item mobile={2.4}>
              <Box
                sx={{
                  borderRadius: 1,
                  p: 1,
                  bgcolor: "#EAF3EB",
                  height: 50,
                }}
              >
                <Typography
                  sx={{
                    color: muiTheme.colorSchemes.light.palette.neutral[700],
                    ...muiTheme.typography.h4_med,
                  }}
                >
                  Q Total
                </Typography>
              </Box>
            </Grid>

            {isLoadingOrganizationTargets ? (
              <Grid item mobile={12}>
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
                <Skeleton
                  sx={{ borderRadius: 1, marginBottom: 1 }}
                  height={50}
                  variant="rectangular"
                />
              </Grid>
            ) : (
              <>
                {/* Department rows */}
                {eligibleSubDepartmentsForTargets.map((dept: SubDepartment) => {
                  const departmentData = organizePeriodData.departments.find(
                    (d) => d.uid === dept.uid
                  );
                  const hasExistingTargets = Boolean(departmentData);

                  return (
                    <React.Fragment key={dept.uid}>
                      <Grid item container spacing={1}>
                        <Grid item mobile={2.4}>
                          <Box
                            sx={{
                              borderRadius: 1,
                              p: 1,
                              bgcolor:
                                muiTheme.colorSchemes.light.palette.neutral[50],
                              height: 50,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  muiTheme.colorSchemes.light.palette
                                    .neutral[700],
                                ...muiTheme.typography.h4_med,
                                textTransform: "capitalize",
                              }}
                            >
                              {dept.name}
                            </Typography>
                          </Box>
                        </Grid>
                        {!hasExistingTargets ? (
                          <Grid item mobile={9.6}>
                            {!viewOnly ? (
                              <Button
                                variant="outlined"
                                fullWidth
                                startIcon={
                                  <PlusCircle
                                    style={{
                                      color:
                                        muiTheme.colorSchemes.light.palette
                                          .primary[500],
                                    }}
                                  />
                                }
                                sx={{
                                  color:
                                    muiTheme.colorSchemes.light.palette
                                      .primary[500],
                                  borderColor:
                                    muiTheme.colorSchemes.light.palette
                                      .neutral[100],
                                  bgcolor: "#F9F9F9",
                                  minHeight: 50,
                                  textTransform: "none",
                                  "&:hover": {
                                    borderColor:
                                      muiTheme.colorSchemes.light.palette
                                        .primary[500],
                                    bgcolor:
                                      muiTheme.colorSchemes.light.palette
                                        .neutral[100],
                                  },
                                  "&:active": {
                                    bgcolor:
                                      muiTheme.colorSchemes.light.palette
                                        .neutral[50],
                                  },
                                }}
                                onClick={() =>
                                  onCreateOrganizationTarget({
                                    subDepartmentUId: dept.uid,
                                    subDepartmentName: dept.name,
                                  })
                                }
                              >
                                Create
                              </Button>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "100%",
                                  backgroundColor: "#F9F9F9",
                                }}
                              >
                                <Typography
                                  sx={{
                                    color:
                                      muiTheme.colorSchemes.light.palette
                                        .neutral[700],
                                    ...muiTheme.typography.h4_med,
                                  }}
                                >
                                  No targets found for this quarter
                                </Typography>
                              </Box>
                            )}
                          </Grid>
                        ) : (
                          <>
                            <Grid item mobile={2.4}>
                              <Box
                                sx={{
                                  p: 1,
                                  pl: 2,
                                  height: 50,
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#EAF3EB",
                                  borderRadius: 1,
                                }}
                              >
                                {departmentData?.m1.toLocaleString()} EGP
                              </Box>
                            </Grid>
                            <Grid item mobile={2.4}>
                              <Box
                                sx={{
                                  p: 1,
                                  pl: 2,
                                  height: 50,
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#EAF3EB",
                                  borderRadius: 1,
                                }}
                              >
                                {departmentData?.m2.toLocaleString()} EGP
                              </Box>
                            </Grid>
                            <Grid item mobile={2.4}>
                              <Box
                                sx={{
                                  p: 1,
                                  pl: 2,
                                  height: 50,
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#EAF3EB",
                                  borderRadius: 1,
                                }}
                              >
                                {departmentData?.m3.toLocaleString()} EGP
                              </Box>
                            </Grid>
                            <Grid item mobile={2.4}>
                              <Box
                                sx={{
                                  p: 1,
                                  pl: 2,
                                  height: 50,
                                  display: "flex",
                                  alignItems: "center",
                                  bgcolor: "#EAF3EB",
                                  borderRadius: 1,
                                }}
                              >
                                {departmentData?.total.toLocaleString()} EGP
                              </Box>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </React.Fragment>
                  );
                })}
              </>
            )}
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export default WithErrorHandling(OrganizationTarget);
